import { Box, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback, useMemo, useState } from "react";

import type { TestEmailPayload } from "@/api/dragon-tiles/email";
import type { Frame } from "@/types/dragon-tiles";

import { frameQueryOptions } from "@/api/dragon-tiles";
import { AbTestToggle } from "@/features/BroadcastV2/components/AbTestToggle";
import { EmptyPreviewTextConfirmModal } from "@/features/BroadcastV2/components/EmptyPreviewTextConfirmModal";
import { BroadcastAbSettingsSection } from "@/features/BroadcastV2/components/sections/BroadcastAbSettingsSection";
import { BroadcastCancelScheduleSection } from "@/features/BroadcastV2/components/sections/BroadcastCancelScheduleSection";
import { BroadcastConfirmSection } from "@/features/BroadcastV2/components/sections/BroadcastConfirmSection";
import { BroadcastScheduleSection } from "@/features/BroadcastV2/components/sections/BroadcastScheduleSection";
import { BroadcastSendDetailsSection } from "@/features/BroadcastV2/components/sections/BroadcastSendDetailsSection";
import { BroadcastEmailDetailsFields } from "@/features/BroadcastV2/components/shared/BroadcastEmailDetailsFields";
import { SuccessAnimation } from "@/features/BroadcastV2/components/shared/SuccessAnimation";
import { VariationTabs } from "@/features/BroadcastV2/components/VariationTabs";
import { broadcastV2ComposeEmailFormSchemaV2 } from "@/features/BroadcastV2/createBroadcastPayload";
import {
  frameSubjectPreviewWarning,
  missingSubjectLineMessage,
  type EmptyFieldWarning,
} from "@/features/BroadcastV2/emptySubjectPreview";
import {
  useIsEmailFrequencyCapOverrideEnabledForSite,
  useIsMultiVariationEnabledForSite,
  useIsSmartSendEnabledForSite,
} from "@/features/BroadcastV2/flag";
import {
  useBroadcastComposeActions,
  useBroadcastComposeStore,
  useBroadcastComposeStoreApi,
  useBroadcastComposeStoreShallow,
  useIsAbDurationInvalid,
} from "@/features/BroadcastV2/store/broadcastComposeStore";
import { isCampaignLocked } from "@/features/BroadcastV2/store/broadcastComposeStoreFactory";
import {
  MAX_VARIATIONS,
  variationLabel,
  type VariationDraft,
} from "@/features/BroadcastV2/store/variationTypes";
import { type AudiencesBroadcastStatus } from "@/features/BroadcastV2/types";

export type BroadcastV2TestEmailPayloadFields = Pick<
  TestEmailPayload,
  "from_name" | "from_email" | "reply_to"
>;

export interface BroadcastV2ComposeEmailEditCallbacks {
  // Resolve false when the user cancelled (skip success feedback), else void.
  onSaveDraft: () => Promise<void | boolean>;
  onActivate: () => Promise<void>;
  onCancelSchedule?: () => Promise<void>;
}

export interface BroadcastV2ComposeEmailFormProps {
  broadcastStatus?: AudiencesBroadcastStatus;
  editCallbacks?: BroadcastV2ComposeEmailEditCallbacks;
  onActivate?: () => Promise<void>;
  /**
   * Prompts about unsaved subject/preheader edits on the active tile. Runs
   * before the blank subject/preview check so it sees the copy that will be
   * sent. Resolves false when the user cancelled.
   */
  flushPendingSubjectPreheader?: () => Promise<boolean>;
  /** Flashes the active tile's subject line field when it is blank. */
  flagBlankSubjectLine?: () => void;
  /**
   * Asks before the active tile leaves the editor (switching, adding, or
   * removing variations), as if navigating away. Resolves true to go ahead.
   */
  confirmLeaveTile?: () => Promise<boolean>;
  /** Store timezone for the schedule picker (display/interpret in this zone). */
  timezone?: string;
}

/** Flattens zod issue paths into dotted keys (e.g. "variations.0.fromName"). */
function mapZodErrors(error: {
  issues: Array<{ path: (string | number)[]; message: string }>;
}): Record<string, string> {
  const result: Record<string, string> = {};
  for (const issue of error.issues) {
    const key = issue.path.join(".");
    if (key && !result[key]) {
      result[key] = issue.message;
    }
  }
  return result;
}

export function BroadcastV2ComposeEmailForm({
  broadcastStatus,
  editCallbacks,
  onActivate,
  flushPendingSubjectPreheader,
  flagBlankSubjectLine,
  confirmLeaveTile,
  timezone,
}: BroadcastV2ComposeEmailFormProps) {
  const isEditMode = editCallbacks !== undefined;
  const isDraft =
    !broadcastStatus || broadcastStatus === "BROADCAST_STATUS_DRAFT";
  const isActive = broadcastStatus === "BROADCAST_STATUS_ACTIVE";
  const isReadonly = isEditMode && !isDraft;
  const canCancelScheduledBroadcast =
    isActive && Boolean(editCallbacks?.onCancelSchedule);

  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Non-null while the empty preview confirmation is open (FOG-2048).
  const [pendingWarnings, setPendingWarnings] = useState<
    EmptyFieldWarning[] | null
  >(null);

  const { siteId = "" } = useParams({ strict: false });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const storeApi = useBroadcastComposeStoreApi();
  const actions = useBroadcastComposeActions();

  const name = useBroadcastComposeStore((s) => s.name);
  const nameError = useBroadcastComposeStore((s) => s.fieldErrors.name ?? "");
  const audienceOptions = useBroadcastComposeStore((s) => s.audienceOptions);
  const excludedAudienceOptions = useBroadcastComposeStoreShallow(
    (s) => s.excludedAudienceOptions,
  );
  const audienceError = useBroadcastComposeStore(
    (s) => s.fieldErrors.audienceIds ?? "",
  );
  const sendMode = useBroadcastComposeStore((s) => s.sendMode);
  const smartSend = useBroadcastComposeStore((s) => s.smartSend);
  const reachOptimization = useBroadcastComposeStore(
    (s) => s.reachOptimization,
  );
  const reachOptimizationPercentage = useBroadcastComposeStore(
    (s) => s.reachOptimizationPercentage,
  );
  const enforceFrequencyCap = useBroadcastComposeStore(
    (s) => s.enforceFrequencyCap,
  );
  const scheduledAtIso = useBroadcastComposeStore((s) => s.scheduledAtIso);
  const scheduleWindow = useBroadcastComposeStoreShallow(
    (s) => s.scheduleWindow,
  );
  const calendarOwned = useBroadcastComposeStore((s) => s.calendarOwned);
  const campaignLocked = isCampaignLocked(calendarOwned, scheduleWindow);
  const scheduledAtError = useBroadcastComposeStore(
    (s) => s.fieldErrors.scheduledAtIso ?? "",
  );
  const confirmSend = useBroadcastComposeStore((s) => s.confirmSend);
  const confirmSendError = useBroadcastComposeStore(
    (s) => s.fieldErrors.confirmSend ?? "",
  );

  const variations = useBroadcastComposeStoreShallow((s) => s.variations);
  const activeVariationIndex = useBroadcastComposeStore(
    (s) => s.activeVariationIndex,
  );
  const fieldErrors = useBroadcastComposeStoreShallow((s) => s.fieldErrors);
  const abTest = useBroadcastComposeStoreShallow((s) => s.abTest);
  const abEnabled = variations.length > 1;
  const multiVariationAllowed = useIsMultiVariationEnabledForSite(siteId);
  const maxVariations = multiVariationAllowed ? MAX_VARIATIONS : 2;
  const smartSendAllowed = useIsSmartSendEnabledForSite(siteId);
  const frequencyCapOverrideAllowed =
    useIsEmailFrequencyCapOverrideEnabledForSite(siteId);
  const activeEmail = variations[activeVariationIndex]?.email;

  const errPrefix = `variations.${activeVariationIndex}`;
  const fromNameError = fieldErrors[`${errPrefix}.fromName`] ?? "";
  const fromEmailError = fieldErrors[`${errPrefix}.fromEmail`] ?? "";
  const replyToError = fieldErrors[`${errPrefix}.replyTo`] ?? "";

  const resolvedFromName = activeEmail?.fromName ?? "";
  const resolvedFromEmail = activeEmail?.fromEmail ?? "";
  const resolvedReplyTo = activeEmail?.replyTo ?? "";

  // Lock the domain to the first variation's email domain (stable per mount).
  const lockedDomain = useMemo(() => {
    const email = (
      storeApi.getState().variations[0]?.email?.fromEmail ?? ""
    ).trim();
    if (!email) return "";
    const at = email.lastIndexOf("@");
    return at === -1 ? "" : email.slice(at + 1).trim();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSuccessAnimationComplete = useCallback(() => {
    setShowSuccessAnimation(false);
    void navigate({
      to: "/dragon-tiles/$siteId/broadcast/list",
      params: { siteId },
    });
  }, [navigate, siteId]);

  // A/B is derived from the variation count. Turning it off parks the extra
  // variations (preserved in-session, but not sent/saved) so an accidental
  // toggle doesn't wipe work; turning it back on restores them, or adds the
  // first extra variation if there's nothing parked.
  // Run `change` once the active tile may leave the editor.
  const leaveTileThen = useCallback(
    (change: () => void) => {
      if (!confirmLeaveTile) return change();
      void confirmLeaveTile().then((leave) => {
        if (leave) change();
      });
    },
    [confirmLeaveTile],
  );

  const handleAbToggle = useCallback(
    (enabled: boolean) => {
      if (enabled) {
        actions.setSmartSend(false);
        if (storeApi.getState().stashedVariations.length > 0) {
          actions.restoreStashedVariations();
        } else {
          actions.addVariation();
        }
        // Default to viewing Variation A.
        actions.setActiveVariation(0);
      } else if (storeApi.getState().activeVariationIndex === 0) {
        actions.stashExtraVariations();
      } else {
        // Turning A/B off moves the editor back to Variation A.
        leaveTileThen(() => actions.stashExtraVariations());
      }
    },
    [actions, storeApi, leaveTileThen],
  );

  async function runActivate() {
    setPendingWarnings(null);
    setIsSubmitting(true);
    try {
      if (isEditMode) {
        await editCallbacks.onActivate();
      } else {
        await onActivate?.();
      }
      setShowSuccessAnimation(true);
    } catch {
      // Errors shown by callbacks
    } finally {
      setIsSubmitting(false);
    }
  }

  // Fetch each variation's current frame (inline subject edits invalidate the
  // ["frame", ...] query but don't write back into the store snapshot, so the
  // snapshot can be stale) and collect empty subject/preview warnings.
  async function collectEmptyWarnings(
    variations: VariationDraft[],
  ): Promise<EmptyFieldWarning[]> {
    const isAb = variations.length > 1;
    const results = await Promise.all(
      variations.map(async (v, i) => {
        const frameId = v.email?.frameId ?? v.frameId;
        if (!frameId) return null;
        let frame: Frame | null;
        try {
          frame = await queryClient.fetchQuery(
            frameQueryOptions(siteId, frameId),
          );
        } catch {
          frame = v.email?.selectedFrame ?? null; // fall back to snapshot
        }
        return frameSubjectPreviewWarning(frame, isAb ? variationLabel(i) : "");
      }),
    );
    return results.filter((w): w is EmptyFieldWarning => w !== null);
  }

  async function handleSubmit() {
    const state = storeApi.getState();
    const parsed = broadcastV2ComposeEmailFormSchemaV2.safeParse({
      name: state.name,
      audienceIds: state.audienceIds,
      sendMode: state.sendMode,
      scheduledAtIso: state.scheduledAtIso,
      confirmSend: state.confirmSend,
      variations: state.variations.map((v) => ({
        frameId: v.email?.frameId ?? v.frameId,
        fromName: v.email?.fromName ?? "",
        fromEmail: v.email?.fromEmail ?? "",
        replyTo: v.email?.replyTo ?? "",
      })),
    });

    if (!parsed.success) {
      state.actions.setFieldErrors(mapZodErrors(parsed.error));
      enqueueSnackbar(parsed.error.issues[0]?.message ?? "Check the form", {
        variant: "error",
      });
      return;
    }

    // Busy from here so Complete can't start a second submit while the
    // unsaved-copy prompt or its tile save is in flight.
    setIsSubmitting(true);
    let warnings: EmptyFieldWarning[];
    try {
      if ((await flushPendingSubjectPreheader?.()) === false) return;
      warnings = await collectEmptyWarnings(state.variations);
    } catch {
      return; // A failed tile save already showed its error.
    } finally {
      setIsSubmitting(false);
    }
    const missingSubject = warnings.filter((w) => w.missingSubject);
    if (missingSubject.length > 0) {
      enqueueSnackbar(
        missingSubjectLineMessage(missingSubject, state.sendMode),
        {
          variant: "error",
        },
      );
      flagBlankSubjectLine?.();
      return;
    }
    if (warnings.length > 0) {
      setPendingWarnings(warnings);
      return;
    }

    await runActivate();
  }

  const isConfirmTyped = confirmSend === "CONFIRM";
  const hasFrame =
    isEditMode ||
    (abEnabled
      ? variations.every((v) => v.frameId != null)
      : variations[0]?.frameId != null);
  const abDurationInvalid = useIsAbDurationInvalid();

  return (
    <>
      <Stack
        component="form"
        gap={4}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void handleSubmit();
        }}
        sx={{ flex: 1, minHeight: 0 }}
      >
        <BroadcastSendDetailsSection
          siteId={siteId}
          medium="MEDIUM_TYPE_EMAIL"
          name={name}
          onNameChange={actions.setName}
          nameError={nameError || undefined}
          audienceOptions={audienceOptions}
          onAudiencesChange={(opts) =>
            actions.setAudiences(
              opts.map((o) => o.id),
              opts,
            )
          }
          audienceError={audienceError || undefined}
          excludedAudienceOptions={excludedAudienceOptions}
          onExcludedAudiencesChange={(opts) =>
            actions.setExcludedAudiences(
              opts.map((o) => o.id),
              opts,
            )
          }
          disabled={isReadonly}
          reachOptimization={reachOptimization}
          onReachOptimizationChange={actions.setReachOptimization}
          reachOptimizationPercentage={reachOptimizationPercentage}
          onReachOptimizationPercentageChange={
            actions.setReachOptimizationPercentage
          }
          showEnforceFrequencyCap={frequencyCapOverrideAllowed}
          enforceFrequencyCap={enforceFrequencyCap}
          onEnforceFrequencyCapChange={actions.setEnforceFrequencyCap}
        />

        {/* Email Details */}
        <Stack
          gap={4}
          sx={{
            backgroundColor: (theme) =>
              theme.palette.background["paper-elevation-0"],
            borderRadius: (theme) => `${theme.shape.borderRadiusMd}px`,
            p: 6,
          }}
        >
          <Typography variant="subtitle1">Email Details</Typography>
          <AbTestToggle
            checked={abEnabled}
            onChange={handleAbToggle}
            disabled={isReadonly}
          />
          <Box>
            {abEnabled && (
              <VariationTabs
                items={variations.map((v, i) => ({
                  id: v.id,
                  label: v.email?.selectedFrame?.name || variationLabel(i),
                  removable: i > 0,
                }))}
                activeIndex={activeVariationIndex}
                onSelect={(index) => {
                  if (index === activeVariationIndex) return;
                  leaveTileThen(() => actions.setActiveVariation(index));
                }}
                onAdd={
                  isReadonly || variations.length >= maxVariations
                    ? undefined
                    : () => leaveTileThen(() => actions.addVariation())
                }
                onRemove={(index) => {
                  // Removing a tab at or before the active one changes which
                  // tile is shown.
                  if (index > activeVariationIndex) {
                    actions.removeVariation(index);
                    return;
                  }
                  leaveTileThen(() => actions.removeVariation(index));
                }}
                disabled={isReadonly}
              />
            )}
            <Stack
              gap={4}
              sx={(theme) => ({
                backgroundColor: theme.palette.background["paper-elevation-1"],
                borderRadius: `${theme.shape.borderRadiusMd}px`,
                borderTopLeftRadius: 0,
                p: 6,
              })}
            >
              <BroadcastEmailDetailsFields
                fromName={resolvedFromName}
                onFromNameChange={(value: string) =>
                  actions.updateVariationEmail(activeVariationIndex, {
                    fromName: value,
                  })
                }
                fromEmail={resolvedFromEmail}
                onFromEmailChange={(value: string) =>
                  actions.updateVariationEmail(activeVariationIndex, {
                    fromEmail: value,
                  })
                }
                replyTo={resolvedReplyTo}
                onReplyToChange={(value: string) =>
                  actions.updateVariationEmail(activeVariationIndex, {
                    replyTo: value,
                  })
                }
                lockedDomain={lockedDomain}
                disabled={isReadonly}
                fromNameError={fromNameError}
                fromEmailError={fromEmailError}
                replyToError={replyToError}
              />
            </Stack>
          </Box>
          {abEnabled && (
            <BroadcastAbSettingsSection
              winningMetric={abTest.winningMetric}
              onWinningMetricChange={actions.setWinningMetric}
              testSizePercent={abTest.testSizePercent}
              onTestSizePercentChange={actions.setTestSizePercent}
              testDurationValue={abTest.testDurationValue}
              onTestDurationValueChange={actions.setTestDurationValue}
              testDurationUnit={abTest.testDurationUnit}
              onTestDurationUnitChange={actions.setTestDurationUnit}
              disabled={isReadonly}
              tileCount={variations.length}
            />
          )}
        </Stack>

        <BroadcastScheduleSection
          sendMode={sendMode}
          scheduleWindow={scheduleWindow}
          calendarOwned={calendarOwned}
          onSendModeChange={actions.setSendMode}
          scheduledAtIso={scheduledAtIso}
          onScheduledAtChange={actions.setScheduledAtIso}
          scheduleError={scheduledAtError || undefined}
          disabled={isReadonly}
          timezone={timezone}
          showSmartSend={smartSendAllowed}
          smartSend={smartSend}
          smartSendDisabledReason={
            abEnabled ? "Smart Send isn't available for A/B tests." : undefined
          }
          onSmartSendChange={actions.setSmartSend}
        />

        {!isReadonly && (
          <BroadcastConfirmSection
            confirmValue={confirmSend}
            onConfirmChange={actions.setConfirmSend}
            confirmError={confirmSendError || undefined}
            onSubmit={() => void handleSubmit()}
            // Campaign broadcasts are activated by scheduling the campaign,
            // never sent directly from the composer.
            disabled={campaignLocked}
            submitDisabled={
              isSubmitting || !isConfirmTyped || !hasFrame || abDurationInvalid
            }
            submitBusy={isSubmitting}
            submitDisabledReason={
              campaignLocked
                ? "Campaign broadcasts are sent by scheduling the campaign"
                : !hasFrame
                  ? "Select a tile from the right panel"
                  : abDurationInvalid
                    ? "Test duration must be at least 4 hours"
                    : undefined
            }
          />
        )}

        {canCancelScheduledBroadcast && (
          <BroadcastCancelScheduleSection
            onCancelSchedule={() => void editCallbacks?.onCancelSchedule?.()}
          />
        )}
      </Stack>

      <EmptyPreviewTextConfirmModal
        open={pendingWarnings !== null}
        warnings={pendingWarnings ?? []}
        isSubmitting={isSubmitting}
        confirmLabel={
          sendMode === "instant" ? "Send anyway" : "Schedule anyway"
        }
        onCancel={() => setPendingWarnings(null)}
        onConfirm={() => void runActivate()}
      />

      <SuccessAnimation
        isVisible={showSuccessAnimation}
        onComplete={handleSuccessAnimationComplete}
      />
    </>
  );
}
